/**
 * Dataset review: a folder of subjects, answered all at once.
 *
 * The thing this replaces is a person opening ninety-six folders in turn to
 * see how many files each has, which are missing, and whether the headers look
 * right. That is a real afternoon, it is done by whoever cannot write the
 * script, and it does not even find the worst problem — one subject acquired
 * on a different matrix is invisible from inside that subject. You only see it
 * by comparing, which is precisely what opening them one at a time cannot do.
 *
 * So the screen is arranged as the questions arrive:
 *
 *   **Can I run this?** One line: how many subjects, how many ready, how many
 *   will fail. If that line is all good news, the rest is not needed today.
 *
 *   **What is missing, and from whom?** Coverage per role, with the subjects
 *   named — not a percentage, because you cannot open a percentage.
 *
 *   **Does anything disagree?** The geometry each role was acquired at, how
 *   many subjects share it, and who does not. The cohort's own majority is the
 *   reference; there is no table of correct values here, because there isn't
 *   one.
 *
 *   **Show me the subjects.** A row each, filterable to the ones that matter,
 *   each expanding to its files and its findings.
 *
 * And then the two things the afternoon was for: **run the ready ones** as a
 * batch, and **export the table**, because the person who needs it next is not
 * sitting at this computer.
 */

import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Download,
  FileText,
  FolderOpen,
  Loader2,
  Play,
  RefreshCw,
  Search,
  ShieldCheck,
  XCircle,
} from "lucide-react";

import { Button, EmptyState, Input } from "@/components/ui/primitives";
import {
  cancelCohortIntegrity,
  checkCohortIntegrity,
  onIntegrityProgress,
  pickDirectory,
  revealPath,
  scanCohort,
  splitError,
} from "@/ipc/api";
import type {
  CohortScan,
  CohortSubject,
  IntegrityProgress,
  Severity,
} from "@/ipc/contract";
import { cn } from "@/lib/utils";
import { goToTab } from "@/pipeline/actions";
import { usePipelineSession } from "@/pipeline/session";
import { saveTextAsFile } from "@/results/clipboard";
import { useAppStore } from "@/store/appStore";
import { PathField } from "@/components/PathField";
import { CompletenessMatrix, StripPlot } from "./charts";
import {
  applyFilter,
  countFor,
  csvFileName,
  FILTERS,
  formatBytes,
  rolesInUse,
  toCsv,
  topIssues,
  type CohortFilter,
} from "./review";
import { buildReport, reportFileName } from "./report";

const SEVERITY_ICON: Record<Severity, typeof CheckCircle2> = {
  ok: CheckCircle2,
  warning: AlertTriangle,
  error: XCircle,
};

const SEVERITY_CLASS: Record<Severity, string> = {
  ok: "text-emerald-600",
  warning: "text-amber-600",
  error: "text-red-600",
};

/* ------------------------------------------------------------- headline --- */

function Headline({ scan }: { scan: CohortScan }) {
  const blocked = scan.withErrors;
  const tone = blocked > 0 ? "error" : scan.withWarnings > 0 ? "warning" : "ok";
  return (
    <div
      className={cn(
        "rounded-xl border px-5 py-4",
        tone === "error"
          ? "border-red-200 bg-red-50"
          : tone === "warning"
            ? "border-amber-200 bg-amber-50"
            : "border-emerald-200 bg-emerald-50",
      )}
    >
      <p className="text-[13.5px] font-medium leading-relaxed text-neutral-900">
        {blocked > 0 ? (
          <>
            {scan.dwiReadyCount} of {scan.subjectCount} subjects can run now.{" "}
            <span className="text-red-700">
              {blocked} {blocked === 1 ? "has a problem" : "have problems"} that would make the run
              fail
            </span>{" "}
            — they are listed under “Will fail”.
          </>
        ) : scan.withWarnings > 0 ? (
          <>
            All {scan.subjectCount} subjects can run. {scan.withWarnings}{" "}
            {scan.withWarnings === 1 ? "has something" : "have something"} worth looking at first.
          </>
        ) : (
          <>All {scan.subjectCount} subjects are complete and consistent. Nothing needs attention.</>
        )}
      </p>
      <p className="mt-1.5 text-[11px] text-neutral-600">
        {formatBytes(scan.totalBytes)} · read {new Date(scan.scannedIso).toLocaleString()}
      </p>
    </div>
  );
}

/* ------------------------------------------------------------- coverage --- */

function Coverage({ scan }: { scan: CohortScan }) {
  return (
    <div className="overflow-hidden rounded-xl border border-ads-border bg-ads-surface">
      {scan.coverage.map((c) => {
        const missing = c.missingSubjectIds;
        const pct = Math.round((c.present / Math.max(scan.subjectCount, 1)) * 100);
        return (
          <div key={c.role} className="border-b border-ads-border px-4 py-2.5 last:border-0">
            <div className="flex items-baseline gap-3">
              <span className="w-14 shrink-0 text-[12px] font-semibold text-neutral-900">
                {c.role}
              </span>
              <span className="w-24 shrink-0 text-[11.5px] tabular-nums text-neutral-600">
                {c.present} / {scan.subjectCount}
              </span>
              <span className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-ads-bg">
                <span
                  className={cn("block h-full rounded-full", pct === 100 ? "bg-emerald-500" : "bg-amber-500")}
                  style={{ width: `${pct}%` }}
                />
              </span>
            </div>
            {missing.length ? (
              <p className="mt-1.5 pl-[4.25rem] text-[11px] leading-relaxed text-neutral-500">
                Missing from{" "}
                <span className="font-mono text-[10.5px] text-neutral-700">
                  {missing.slice(0, 8).join(", ")}
                </span>
                {missing.length > 8 ? ` and ${missing.length - 8} more` : ""}
              </p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/* ---------------------------------------------------------- consistency --- */

function Consistency({ scan }: { scan: CohortScan }) {
  const interesting = scan.consistency.filter((g) => g.variants.length > 1);
  if (interesting.length === 0) {
    return (
      <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[11.5px] leading-relaxed text-emerald-900">
        Every subject was acquired the same way, for every image type. There is nothing here that
        would make one subject behave differently from another.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      {interesting.map((g) => (
        <div key={g.role} className="overflow-hidden rounded-xl border border-ads-border bg-ads-surface">
          <div className="flex items-baseline gap-2 border-b border-ads-border px-4 py-2">
            <span className="text-[12px] font-semibold text-neutral-900">{g.role}</span>
            <span className="text-[11px] text-neutral-500">
              {g.common
                ? `${g.agreeing} of ${g.present} subjects agree`
                : `${g.present} subjects, no single geometry in the majority`}
            </span>
          </div>
          {g.variants.map((v, i) => {
            const isCommon = g.common != null && i === 0;
            return (
              <div
                key={v.description}
                className="flex items-baseline gap-3 border-b border-ads-border px-4 py-2 last:border-0"
              >
                <span
                  className={cn(
                    "w-10 shrink-0 text-[11px] font-semibold tabular-nums",
                    isCommon ? "text-neutral-500" : "text-amber-700",
                  )}
                >
                  {v.subjectIds.length}×
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-mono text-[11px] text-neutral-800">
                    {v.description}
                  </span>
                  {!isCommon ? (
                    <span className="mt-0.5 block font-mono text-[10.5px] text-amber-700">
                      {v.subjectIds.slice(0, 10).join(", ")}
                      {v.subjectIds.length > 10 ? ` and ${v.subjectIds.length - 10} more` : ""}
                    </span>
                  ) : null}
                </span>
                {isCommon ? (
                  <span className="shrink-0 rounded-full bg-ads-bg px-2 py-0.5 text-[10px] text-neutral-500">
                    the cohort
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/* --------------------------------------------------------------- naming --- */

/**
 * How the files are named, and the consequence.
 *
 * The consequence is the point. OpenADS locates its inputs by filename, so a
 * file outside the patterns is not found and nothing is reported — the stage
 * proceeds without it, or fails much later for a reason that does not mention
 * naming. Saying "three subjects' DWI will not be found" before an hour of
 * compute is the whole return on reading the names.
 */
function Naming({ scan }: { scan: CohortScan }) {
  const { subjectPatterns, filePatterns, unreachable } = scan.naming;
  const byRole = new Map<string, typeof unreachable>();
  for (const u of unreachable) {
    byRole.set(u.role, [...(byRole.get(u.role) ?? []), u]);
  }

  return (
    <div className="flex flex-col gap-3">
      {unreachable.length ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-[12px] font-medium leading-relaxed text-amber-900">
            {[...byRole.entries()]
              .map(([role, list]) => `${list.length} ${role}`)
              .join(", ")}{" "}
            {unreachable.length === 1 ? "file" : "files"} will not be found by the pipeline as they
            are named.
          </p>
          <ul className="mt-1.5 space-y-0.5">
            {unreachable.slice(0, 8).map((u) => (
              <li key={`${u.subjectId}-${u.role}`} className="text-[11px] text-amber-900">
                <span className="font-mono">{u.subjectId}</span> ·{" "}
                <span className="font-mono">{u.fileName}</span> would have to be called{" "}
                <span className="font-mono">{u.expected}</span>
              </li>
            ))}
            {unreachable.length > 8 ? (
              <li className="text-[11px] text-amber-800">and {unreachable.length - 8} more</li>
            ) : null}
          </ul>
        </div>
      ) : (
        <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-[11.5px] leading-relaxed text-emerald-900">
          Every file the pipeline needs is named in a way it will find.
        </p>
      )}

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {[
          { title: "Subject folder names", patterns: subjectPatterns },
          { title: "File names", patterns: filePatterns },
        ].map(({ title, patterns }) => (
          <div key={title} className="overflow-hidden rounded-xl border border-ads-border bg-ads-surface">
            <div className="border-b border-ads-border bg-ads-subtle px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-400">
              {title}
            </div>
            {patterns.slice(0, 10).map((p, i) => (
              <div
                key={p.pattern}
                className="flex items-baseline gap-3 border-b border-ads-border px-4 py-1.5 last:border-0"
              >
                <span className="w-10 shrink-0 text-right text-[11px] font-semibold tabular-nums text-neutral-500">
                  {p.count}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-mono text-[11px] text-neutral-800">
                    {p.pattern}
                  </span>
                  {/* The minority is named in full; the majority does not need
                      examples, it needs to be recognised and passed over. */}
                  {i > 0 && p.count <= 4 ? (
                    <span className="block truncate font-mono text-[10px] text-amber-700">
                      {p.members.join(", ")}
                    </span>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- subject --- */

function SubjectRow({ subject, roles }: { subject: CohortSubject; roles: string[] }) {
  const [open, setOpen] = useState(false);
  const Icon = SEVERITY_ICON[subject.severity];
  return (
    <div className="border-b border-ads-border last:border-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-ads-subtle"
      >
        <ChevronRight
          className={cn("h-3.5 w-3.5 shrink-0 text-neutral-300 transition", open && "rotate-90")}
        />
        <Icon className={cn("h-3.5 w-3.5 shrink-0", SEVERITY_CLASS[subject.severity])} />
        <span className="w-40 shrink-0 truncate text-[12.5px] font-medium text-neutral-900">
          {subject.subjectId}
        </span>
        {roles.map((role) => (
          <span
            key={role}
            title={role}
            className={cn(
              "w-12 shrink-0 text-center text-[10.5px] font-medium",
              subject.roles.includes(role) ? "text-neutral-700" : "text-neutral-300",
            )}
          >
            {subject.roles.includes(role) ? role : "—"}
          </span>
        ))}
        <span className="w-16 shrink-0 text-right text-[11px] tabular-nums text-neutral-500">
          {subject.fileCount} files
        </span>
        <span className="min-w-0 flex-1 truncate text-[11px] text-neutral-500">
          {topIssues(subject, 1)[0]?.message ?? ""}
        </span>
      </button>

      {open ? (
        <div className="grid grid-cols-1 gap-5 border-t border-ads-border bg-ads-bg px-4 py-3 lg:grid-cols-2">
          <div>
            <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-400">
              Images
            </div>
            {subject.images.length === 0 ? (
              <p className="text-[11px] text-neutral-400">No images found in this folder.</p>
            ) : (
              <ul className="space-y-1">
                {subject.images.map((image) => (
                  <li key={image.fileName} className="flex items-baseline gap-2">
                    <span className="w-12 shrink-0 text-[10.5px] font-medium text-neutral-600">
                      {image.role}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-mono text-[10.5px] text-neutral-700">
                        {image.fileName}
                      </span>
                      <span className="block text-[10px] text-neutral-400">
                        {image.signature
                          ? `${image.signature.dim.join("×")} · ${image.signature.voxelUm
                              .map((u) => (u / 100).toFixed(2))
                              .join("×")} mm · ${image.signature.orientation} · ${image.signature.datatype}`
                          : "header could not be read"}
                        {image.volumes ? ` · ${image.volumes} volumes` : ""}
                        {" · "}
                        {formatBytes(image.sizeBytes)}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <button
              type="button"
              onClick={() => void revealPath(subject.dir).catch(() => undefined)}
              className="mt-2 flex items-center gap-1 text-[10.5px] text-ads-accent hover:underline"
            >
              <FolderOpen className="h-3 w-3" />
              Show folder
            </button>
          </div>
          <div>
            <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-400">
              Findings
            </div>
            {subject.issues.length === 0 ? (
              <p className="text-[11px] text-emerald-700">
                Nothing to report: complete, and consistent with the rest of the cohort.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {subject.issues.map((issue, i) => {
                  const IssueIcon = SEVERITY_ICON[issue.severity];
                  return (
                    <li key={i} className="flex items-start gap-1.5">
                      <IssueIcon
                        className={cn("mt-px h-3 w-3 shrink-0", SEVERITY_CLASS[issue.severity])}
                      />
                      <span className="text-[11px] leading-relaxed text-neutral-700">
                        {issue.message}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------- integrity ------- */

/**
 * The deep check: opening every file.
 *
 * Separate and opt-in because it costs what it costs — every byte of every
 * file is decompressed, which for a cohort of perfusion series is minutes to
 * hours. What it buys is the only view of the faults that live inside a file:
 * a copy that stopped part way, a volume of all zeros, NaNs that make
 * registration wrong without making it fail.
 *
 * Until it has run, the screen says so rather than implying the files are
 * sound because nothing has complained.
 */
function Integrity({
  root,
  onResult,
}: {
  root: string;
  onResult: (r: IntegrityProgress | null) => void;
}) {
  const [state, setState] = useState<IntegrityProgress | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let off: (() => void) | undefined;
    void onIntegrityProgress((e) => {
      setState(e);
      if (e.finished) {
        setRunning(false);
        onResult(e);
      }
    }).then((un) => {
      off = un;
    });
    return () => off?.();
  }, []);

  // A new folder invalidates whatever the last one said.
  useEffect(() => {
    setState(null);
    setRunning(false);
    onResult(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [root]);

  const start = async () => {
    setError(null);
    setState(null);
    setRunning(true);
    try {
      await checkCohortIntegrity(root);
    } catch (e) {
      setRunning(false);
      setError(splitError(e).message);
    }
  };

  const pct = state && state.total > 0 ? Math.round((state.done / state.total) * 100) : 0;
  const findings = state?.finished ? state.findings : [];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {running ? (
          <>
            <Button variant="danger" onClick={() => void cancelCohortIntegrity()}>
              Stop
            </Button>
            <div className="h-1.5 min-w-[160px] flex-1 overflow-hidden rounded-full bg-ads-bg">
              <div className="h-full bg-ads-accent transition-all" style={{ width: `${pct}%` }} />
            </div>
            <span className="shrink-0 text-[11px] tabular-nums text-neutral-600">
              {state?.done ?? 0} / {state?.total ?? 0}
            </span>
            <span className="min-w-0 flex-1 truncate font-mono text-[10.5px] text-neutral-400">
              {state?.current}
            </span>
          </>
        ) : (
          <>
            <Button variant="outline" onClick={() => void start()}>
              <ShieldCheck className="h-3.5 w-3.5" />
              {state ? "Check again" : "Open every file"}
            </Button>
            <span className="text-[11px] text-neutral-500">
              Minutes for a large cohort. It can be stopped.
            </span>
          </>
        )}
      </div>

      {error ? (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[11.5px] text-red-800">
          {error}
        </p>
      ) : null}

      {!state && !running ? (
        // Said plainly: a clean inventory is not evidence the files are sound.
        <p className="rounded-xl border border-ads-border bg-ads-subtle px-4 py-2.5 text-[11.5px] leading-relaxed text-neutral-600">
          Not checked yet. Everything above was read from folder listings and headers — a file can
          pass all of it and still be truncated, empty, or full of NaNs.
        </p>
      ) : null}

      {state?.finished && state.intensity.length ? (
        <div className="rounded-xl border border-ads-border bg-ads-surface px-4 py-2">
          <p className="pb-1 text-[11px] leading-relaxed text-neutral-500">
            Intensity range per subject, by image type. A subject whose values run a thousand times
            higher than the rest is in different units, which no header check sees.
          </p>
          {state.intensity.map((d) => (
            <StripPlot key={d.label} d={d} />
          ))}
        </div>
      ) : null}

      {state?.finished ? (
        findings.length === 0 ? (
          <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-[11.5px] leading-relaxed text-emerald-900">
            {state.cancelled
              ? `Stopped after ${state.done} of ${state.total} files. Nothing wrong in those.`
              : `All ${state.total} files opened and read to the end. Nothing wrong inside any of them.`}
          </p>
        ) : (
          <div className="overflow-hidden rounded-xl border border-amber-200 bg-ads-surface">
            <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-[11.5px] font-medium text-amber-900">
              {findings.length} file{findings.length === 1 ? "" : "s"} of {state.total} have
              something wrong inside them
              {state.cancelled ? " (stopped early)" : ""}
            </div>
            {findings.map((f) => (
              <div key={f.path} className="border-b border-ads-border px-4 py-2 last:border-0">
                <div className="flex items-baseline gap-2">
                  <span className="truncate font-mono text-[11px] text-neutral-800">
                    {f.fileName}
                  </span>
                  {f.min != null ? (
                    <span className="shrink-0 text-[10px] tabular-nums text-neutral-400">
                      {f.min.toPrecision(3)} – {f.max?.toPrecision(3)}
                    </span>
                  ) : null}
                </div>
                {f.problems.map((p, i) => (
                  <p
                    key={i}
                    className={cn(
                      "mt-0.5 text-[11px] leading-relaxed",
                      p.severity === "error" ? "text-red-700" : "text-amber-700",
                    )}
                  >
                    {p.message}
                  </p>
                ))}
              </div>
            ))}
          </div>
        )
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ tab --- */

export function DatasetTab() {
  const [scan, setScan] = useState<CohortScan | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<CohortFilter>("all");
  const [query, setQuery] = useState("");
  const [saved, setSaved] = useState<string | null>(null);
  /** The deep check's result, so the report can say whether it was run. */
  const [integrity, setIntegrity] = useState<IntegrityProgress | null>(null);
  const defaultRoot = useAppStore((s) => s.preferences?.defaultInputRoot ?? null);

  const read = async (dir: string) => {
    setBusy(true);
    setError(null);
    try {
      setScan(await scanCohort(dir));
    } catch (e) {
      setScan(null);
      const { message, detail } = splitError(e);
      setError(detail ? `${message}\n${detail}` : message);
    } finally {
      setBusy(false);
    }
  };

  const choose = async () => {
    const { path } = await pickDirectory("Choose the folder that holds your subjects", defaultRoot ?? undefined);
    if (path) void read(path);
  };

  const rows = useMemo(() => (scan ? applyFilter(scan, filter, query) : []), [scan, filter, query]);
  const roles = useMemo(() => (scan ? rolesInUse(scan) : []), [scan]);

  useEffect(() => setSaved(null), [scan]);

  const exportCsv = async () => {
    if (!scan) return;
    const outcome = await saveTextAsFile(csvFileName(scan), toCsv(scan));
    setSaved(
      outcome === "downloaded"
        ? `Saved ${csvFileName(scan)}`
        : outcome === "copied"
          ? "Could not write the file; the table is on the clipboard instead."
          : "Could not export the table.",
    );
  };

  /**
   * One self-contained file: the charts are SVG already and nothing is
   * fetched, so it opens on a machine that has never seen this app.
   */
  const exportReport = async () => {
    if (!scan) return;
    const name = reportFileName(scan);
    const html = buildReport({
      scan,
      roles,
      integrity: integrity
        ? { findings: integrity.findings, intensity: integrity.intensity, total: integrity.total }
        : null,
    });
    const outcome = await saveTextAsFile(name, html, "text/html;charset=utf-8");
    setSaved(outcome === "downloaded" ? `Saved ${name}` : "Could not write the report.");
  };

  /**
   * Hand the batch runner the subjects this review says can run — not the
   * folder. Pointing it at the root would queue the ones that are about to
   * fail, and the point of having read them is to not do that.
   */
  const runReady = async () => {
    if (!scan) return;
    const session = usePipelineSession.getState();
    session.startNewAnalysis("dwi", "full");
    await session.chooseDatasetRoot(scan.root);
    usePipelineSession
      .getState()
      .selectAllMatched(scan.subjects.filter((s) => s.dwiReady).map((s) => s.subjectId));
    goToTab("pipeline");
  };

  if (!scan) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 bg-ads-bg p-8">
        <EmptyState
          title="Scan a folder of subjects"
          hint="Point at the folder that holds one subfolder per subject. Every one of them is read: what each has, how they are named, which files the pipeline will not find, what disagrees with the rest, and which are ready to run — without opening them one at a time."
        />
        {/* Typed or browsed: a path that arrives in an email should not have
            to be navigated to. */}
        <PathField
          title="Choose the folder that holds your subjects"
          placeholder={defaultRoot ? `${defaultRoot}/…` : "/path/to/your/subjects"}
          busy={busy}
          onSubmit={(path) => void read(path)}
          className="w-full max-w-2xl"
        />
        {error ? (
          <p className="max-w-xl whitespace-pre-line rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-center text-[11.5px] leading-relaxed text-red-800">
            {error}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto bg-ads-bg">
      <div className="mx-auto flex max-w-6xl flex-col gap-7 px-8 py-7">
        <header className="flex items-start justify-between gap-6">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-400">
              Data scan
            </div>
            <h1 className="mt-1.5 truncate text-[20px] font-semibold tracking-tight text-neutral-900">
              {scan.root.split(/[\\/]/).filter(Boolean).pop()}
            </h1>
            <button
              type="button"
              onClick={() => void revealPath(scan.root).catch(() => undefined)}
              className="mt-1 block max-w-full truncate font-mono text-[10.5px] text-neutral-400 hover:text-ads-accent hover:underline"
            >
              {scan.root}
            </button>
          </div>
          <div className="flex shrink-0 gap-1.5">
            <Button size="sm" variant="outline" onClick={() => void read(scan.root)} disabled={busy}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              Re-read
            </Button>
            <Button size="sm" variant="outline" onClick={() => void choose()}>
              <FolderOpen className="h-3.5 w-3.5" />
              Another folder…
            </Button>
            <Button size="sm" variant="outline" onClick={() => void exportCsv()}>
              <Download className="h-3.5 w-3.5" />
              Table (CSV)
            </Button>
            <Button size="sm" variant="outline" onClick={() => void exportReport()}>
              <FileText className="h-3.5 w-3.5" />
              Report (HTML)
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={() => void runReady()}
              disabled={scan.dwiReadyCount === 0}
            >
              <Play className="h-3.5 w-3.5" />
              Run the {scan.dwiReadyCount} that are ready
            </Button>
          </div>
        </header>

        {saved ? <p className="text-[11.5px] text-neutral-500">{saved}</p> : null}

        {/* What the app decided this folder is, and on what evidence. A
            verdict a reader cannot check is one they have to take on faith. */}
        {scan.verdict.evidence.length ? (
          <p className="text-[11px] leading-relaxed text-neutral-500">
            {scan.verdict.evidence.join(" ")}
          </p>
        ) : null}

        <Headline scan={scan} />

        {scan.unreadable.length ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
            <p className="text-[11.5px] font-medium text-red-900">
              {scan.unreadable.length} folder{scan.unreadable.length === 1 ? "" : "s"} could not be
              read at all
            </p>
            <ul className="mt-1.5 space-y-0.5">
              {scan.unreadable.map((u, i) => (
                <li key={i} className="text-[11px] text-red-800">
                  {u.message}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <section>
          <h2 className="mb-2.5 text-[13px] font-semibold text-neutral-900">What each subject has</h2>
          <Coverage scan={scan} />
        </section>

        <section>
          <h2 className="mb-1 text-[13px] font-semibold text-neutral-900">What is inside them</h2>
          <p className="mb-2.5 max-w-3xl text-[11.5px] leading-relaxed text-neutral-500">
            Everything else on this page is read from folder listings and headers. Some faults are
            only visible by decompressing the whole file — which is why this one is asked for.
          </p>
          <Integrity root={scan.root} onResult={setIntegrity} />
        </section>

        <section>
          <h2 className="mb-1 text-[13px] font-semibold text-neutral-900">How they are named</h2>
          <p className="mb-2.5 max-w-3xl text-[11.5px] leading-relaxed text-neutral-500">
            OpenADS finds its inputs by filename. A file outside the patterns it tries is not
            found, and nothing is reported — so this is the cheapest way to know which subjects
            will come out wrong, before any compute is spent.
          </p>
          <Naming scan={scan} />
        </section>

        {scan.distributions.length ? (
          <section>
            <h2 className="mb-1 text-[13px] font-semibold text-neutral-900">How much of it</h2>
            <p className="mb-1 max-w-3xl text-[11.5px] leading-relaxed text-neutral-500">
              Every subject is a point. The ones far from the middle are named — a subject with a
              third of everyone else&rsquo;s files, or a DWI a tenth the usual size, is broken in a
              way no other check sees.
            </p>
            <div className="rounded-xl border border-ads-border bg-ads-surface px-4 py-2">
              {scan.distributions.map((d) => (
                <StripPlot key={d.label} d={d} />
              ))}
            </div>
          </section>
        ) : null}

        <section>
          <h2 className="mb-1 text-[13px] font-semibold text-neutral-900">Completeness</h2>
          <p className="mb-2.5 max-w-3xl text-[11.5px] leading-relaxed text-neutral-500">
            One cell per subject and image type. A column with holes is a modality half the cohort
            lacks; a row that is nearly empty is a subject that should probably not be in it.
          </p>
          <div className="rounded-xl border border-ads-border bg-ads-surface px-4 py-3">
            <CompletenessMatrix scan={scan} roles={roles} onPick={(id: string) => setQuery(id)} />
          </div>
        </section>

        <section>
          <h2 className="mb-1 text-[13px] font-semibold text-neutral-900">
            How they were acquired
          </h2>
          <p className="mb-2.5 max-w-3xl text-[11.5px] leading-relaxed text-neutral-500">
            There is no correct answer here in the abstract — 192×192×38 at 4 mm is right for one
            study and wrong for the next. What matters is whether this cohort agrees with itself,
            so the reference is whatever most of it does.
          </p>
          <Consistency scan={scan} />
        </section>

        <section>
          <div className="mb-2.5 flex flex-wrap items-center gap-2">
            <h2 className="mr-1 text-[13px] font-semibold text-neutral-900">Subjects</h2>
            {FILTERS.map((f) => {
              const n = countFor(scan, f.id);
              const on = f.id === filter;
              return (
                <button
                  key={f.id}
                  type="button"
                  title={f.hint}
                  onClick={() => setFilter(f.id)}
                  className={cn(
                    "flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition",
                    on
                      ? "border-ads-accent-border bg-ads-accent-soft text-ads-accent"
                      : "border-ads-border bg-ads-surface text-neutral-600 hover:border-ads-border-strong",
                  )}
                >
                  {f.label}
                  <span className={cn("tabular-nums", on ? "opacity-70" : "text-neutral-400")}>{n}</span>
                </button>
              );
            })}
            <span className="flex-1" />
            <span className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400" />
              <Input
                className="h-7 w-48 pl-8 text-[11.5px]"
                placeholder="Find a subject…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </span>
          </div>

          <div className="overflow-hidden rounded-xl border border-ads-border bg-ads-surface">
            <div className="flex items-center gap-3 border-b border-ads-border bg-ads-subtle px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-400">
              <span className="w-3.5 shrink-0" />
              <span className="w-3.5 shrink-0" />
              <span className="w-40 shrink-0">Subject</span>
              {roles.map((r) => (
                <span key={r} className="w-12 shrink-0 text-center">
                  {r}
                </span>
              ))}
              <span className="w-16 shrink-0 text-right">Files</span>
              <span className="min-w-0 flex-1">Finding</span>
            </div>
            {rows.length === 0 ? (
              <p className="px-4 py-6 text-center text-[11.5px] text-neutral-500">
                No subject matches this filter.
              </p>
            ) : (
              rows.map((s) => <SubjectRow key={s.subjectId} subject={s} roles={roles} />)
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default DatasetTab;
